import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarSeparator,
} from '@/components/ui/sidebar';
import type { EntryExample, RelationshipTag, Tag } from '@/db/schema';
import { tagsDb } from '@/db/tags';
import { useLiveQuery } from 'dexie-react-hooks';
import { useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

function decodeHtmlEntities(input: string): string {
    if (!input) return '';
    // Decode common named entities first
    let s = input
        .replaceAll('&lt;', '<')
        .replaceAll('&gt;', '>')
        .replaceAll('&quot;', '"')
        .replaceAll('&apos;', "'")
        .replaceAll('&#39;', "'")
        .replaceAll('&nbsp;', ' ');
    // Numeric entities: decimal and hex
    s = s.replace(/&#(\d+);/g, (_, d: string) => String.fromCharCode(parseInt(d, 10)));
    s = s.replace(/&#x([0-9a-fA-F]+);/g, (_, h: string) => String.fromCharCode(parseInt(h, 16)));
    // Finally ampersand, after decoding others
    s = s.replaceAll('&amp;', '&');
    return s;
}

function toSafeHtmlText(input: string): string {
    // After decoding entities to real characters, escape minimal for safe HTML text rendering
    const decoded = decodeHtmlEntities(input);
    return decoded.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');
}

export function RightTagSearchSidebar() {
    const location = useLocation();
    const { tagId } = useParams();
    const tags = useLiveQuery<Tag[]>(() => tagsDb.tags.toArray(), []);

    const selected: Tag | null = useMemo(() => {
        if (!tags || tags.length === 0) return null;
        if (!tagId) return tags[0];
        return tags.find(t => t.id === tagId) ?? tags[0];
    }, [tags, tagId]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [results, setResults] = useState<EntryExample[]>([]);
    const [userParent, setUserParent] = useState<string | null>(null);
    const [userChild, setUserChild] = useState<string | null>(null);
    const [openModalIdx, setOpenModalIdx] = useState<string | null>(null);
    const [copiedKey, setCopiedKey] = useState<string | null>(null);

    const [prevTagId, setPrevTagId] = useState(tagId);
    if (tagId !== prevTagId) {
        setPrevTagId(tagId);
        setUserParent(null);
        setUserChild(null);
    }

    const [prevUserParent, setPrevUserParent] = useState(userParent);
    if (userParent !== prevUserParent) {
        setPrevUserParent(userParent);
        setUserChild(null);
    }

    const allIndexRecords = useLiveQuery<RelationshipTag[]>(() => tagsDb.relationshipTags.toArray(), []);

    const indexCache = useMemo(() => {
        if (!allIndexRecords) return null;
        const cache: Record<string, Record<string, EntryExample[]>> = {};
        for (const record of allIndexRecords) {
            if (!cache[record.parent]) {
                cache[record.parent] = {};
            }
            cache[record.parent][record.child] = record.entries as EntryExample[];
        }
        return cache;
    }, [allIndexRecords]);

    const parentOptions = useMemo(() => {
        return indexCache ? Object.keys(indexCache).sort((a, b) => a.localeCompare(b)) : [];
    }, [indexCache]);

    const defaultParent = useMemo(() => {
        if (!indexCache) return '';
        const routeParent = selected?.id?.toLowerCase() ?? '';
        return parentOptions.includes(routeParent) ? routeParent : (parentOptions[0] ?? '');
    }, [indexCache, selected?.id, parentOptions]);

    const selectedParent = userParent ?? defaultParent;

    const childOptions = useMemo(() => {
        if (!indexCache || !selectedParent) return [] as string[];
        const parentData = indexCache[selectedParent];
        if (!parentData) return [];
        return Object.keys(parentData).sort((a, b) => a.localeCompare(b));
    }, [indexCache, selectedParent]);

    const selectedChild = userChild ?? childOptions[0] ?? '';

    const isTagRoute = location.pathname === '/search' || location.pathname.startsWith('/search/tag/');
    if (!isTagRoute) return null;

    async function onFind() {
        setError(null);
        setResults([]);
        if (!selectedParent || !selectedChild) return;

        try {
            setLoading(true);
            if (!indexCache) {
                setResults([]);
                return;
            }

            const parentData = indexCache[selectedParent];
            if (!parentData) {
                setResults([]);
                return;
            }

            const childLower = selectedChild.toLowerCase();
            // Direct lookup: index already contains top 2 examples per parent-child pair
            const entries = parentData[childLower] ?? [];
            setResults(entries);
        } catch (err: any) {
            setError(err?.message || String(err));
        } finally {
            setLoading(false);
        }
    }

    async function copyDecoded(snippet: string, key: string) {
        const decoded = decodeHtmlEntities(snippet);
        try {
            await navigator.clipboard.writeText(decoded);
            setCopiedKey(key);
            setTimeout(() => setCopiedKey(k => (k === key ? null : k)), 1200);
        } catch {
            // ignore
        }
    }

    return (
        <Sidebar
            side="right"
            collapsible="none"
        >
            <SidebarHeader>
                <div className="px-2 py-1">
                    <div className="text-muted-foreground text-xs">Parent → Child</div>
                    <div className="mt-2 flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                            <Label
                                htmlFor="parent-select"
                                className="text-xs"
                            >
                                Parent
                            </Label>
                            <select
                                id="parent-select"
                                className="rounded border px-2 py-1 text-sm"
                                value={selectedParent}
                                onChange={e => setUserParent(e.target.value)}
                                disabled={!indexCache || loading}
                            >
                                {!indexCache && <option value="">(loading...)</option>}
                                {indexCache && parentOptions.length === 0 && <option value="">(none)</option>}
                                {indexCache &&
                                    parentOptions.map(p => (
                                        <option
                                            key={p}
                                            value={p}
                                        >{`<${p}>`}</option>
                                    ))}
                            </select>
                        </div>
                        <div className="flex items-center gap-2">
                            <Label
                                htmlFor="child-select"
                                className="text-xs"
                            >
                                Child
                            </Label>
                            <select
                                id="child-select"
                                className="rounded border px-2 py-1 text-sm"
                                value={selectedChild}
                                onChange={e => setUserChild(e.target.value)}
                                disabled={!indexCache || !selectedParent || loading}
                            >
                                {!selectedParent && <option value="">(select parent)</option>}
                                {selectedParent && childOptions.length === 0 && <option value="">(none)</option>}
                                {selectedParent &&
                                    childOptions.map(c => (
                                        <option
                                            key={c}
                                            value={c}
                                        >{`<${c}>`}</option>
                                    ))}
                            </select>
                        </div>
                    </div>
                </div>
            </SidebarHeader>
            <SidebarSeparator />
            <SidebarContent>
                <SidebarGroup>
                    <SidebarGroupLabel>Selection</SidebarGroupLabel>
                    <SidebarGroupContent>
                        <div className="text-muted-foreground px-1 text-sm">
                            Choose a parent and a child to find representative examples.
                        </div>
                    </SidebarGroupContent>
                </SidebarGroup>
            </SidebarContent>
            <SidebarFooter>
                <div className="flex flex-col gap-2 px-1">
                    <Button
                        disabled={!selectedParent || !selectedChild || loading}
                        onClick={onFind}
                    >
                        {loading ? 'Finding...' : 'Find'}
                    </Button>
                    {error && <div className="text-xs text-red-600">{error}</div>}
                </div>
                <div className="px-1">
                    {results.length > 0 && (
                        <Accordion
                            type="single"
                            collapsible
                        >
                            {results.map((r, idx) => {
                                const key = `${r.book}-${r.gdocId}-${idx}`;
                                const safe = toSafeHtmlText(r.snippet);
                                return (
                                    <AccordionItem
                                        key={key}
                                        value={key}
                                    >
                                        <AccordionTrigger>
                                            <span className="text-xs font-bold">{r.book}</span>
                                            <span className="text-muted-foreground ml-2 text-xs">(size {r.size})</span>
                                        </AccordionTrigger>
                                        <AccordionContent>
                                            <div className="mb-2 flex items-center gap-2">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => copyDecoded(r.snippet, key)}
                                                >
                                                    {copiedKey === key ? 'Copied' : 'Copy'}
                                                </Button>
                                                <Dialog
                                                    open={openModalIdx === key}
                                                    onOpenChange={open => setOpenModalIdx(open ? key : null)}
                                                >
                                                    <DialogTrigger
                                                        render={
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                            />
                                                        }
                                                    >
                                                        Open
                                                    </DialogTrigger>
                                                    <DialogContent className="max-w-[80vw] sm:max-w-[80vw]">
                                                        <DialogHeader>
                                                            <DialogTitle>{r.book}</DialogTitle>
                                                        </DialogHeader>
                                                        <div className="max-h-[70vh] overflow-auto rounded border bg-gray-50 p-3 text-xs">
                                                            <pre className="whitespace-pre-wrap">
                                                                <code
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: safe,
                                                                    }}
                                                                />
                                                            </pre>
                                                        </div>
                                                        <DialogFooter>
                                                            <Button
                                                                variant="outline"
                                                                onClick={() => copyDecoded(r.snippet, key)}
                                                            >
                                                                {copiedKey === key ? 'Copied' : 'Copy'}
                                                            </Button>
                                                            <DialogClose render={<Button />}>Close</DialogClose>
                                                        </DialogFooter>
                                                    </DialogContent>
                                                </Dialog>
                                            </div>
                                            <pre className="overflow-auto rounded border bg-gray-50 p-2 text-xs whitespace-pre-wrap">
                                                <code
                                                    dangerouslySetInnerHTML={{
                                                        __html: safe,
                                                    }}
                                                />
                                            </pre>
                                            <div className="text-muted-foreground mt-1 text-xs">gdoc: {r.gdocId}</div>
                                        </AccordionContent>
                                    </AccordionItem>
                                );
                            })}
                        </Accordion>
                    )}
                    {results.length === 0 && !loading && <div className="text-muted-foreground text-sm">No results yet.</div>}
                </div>
            </SidebarFooter>
        </Sidebar>
    );
}
